import React, { useEffect } from 'react';
import { gsap } from 'gsap';
import { preloadImages } from '../components/products_js/utils';
import { ContentItem } from '../components/products_js/contentItem';
import { PreviewItem } from '../components/products_js/previewItem';
import '../css_components/products.css'; 

const Products = () => {
  useEffect(() => {
    const bodyEl = document.body;
    const contentOverlayInner = document.querySelector('.content__overlay > .overlay__inner');
    const backCtrl = document.querySelector('.preview__back');

    if (!contentOverlayInner || !backCtrl) return;

    gsap.set(contentOverlayInner, { xPercent: -100 });

    const previewItems = [];
    const contentItems = [];

    document.querySelectorAll('.preview__item').forEach((el) => {
      previewItems.push(new PreviewItem(el));
    });

    document.querySelectorAll('.content__item').forEach((el, i) => {
      contentItems.push(new ContentItem(el, previewItems[i]));
    });

    let current = -1;
    let isAnimating = false;

    contentItems.forEach((contentItem, pos) => {
      const previewItem = previewItems[pos];

      contentItem.DOM.imgWrap.addEventListener('click', () => {
        if (isAnimating) return;
        isAnimating = true;
        current = pos;

        gsap.timeline({
          defaults: { duration: 1.1, ease: 'expo' },
          onStart: () => {
            bodyEl.classList.add('preview-open');
            gsap.set(previewItem.DOM.img, { xPercent: 100 });
            gsap.set(previewItem.DOM.imgWrap, { xPercent: -102, opacity: 0 });
            gsap.set(previewItem.DOM.slideTexts, { yPercent: 100 });
            gsap.set(previewItem.DOM.descriptions, { yPercent: 15, opacity: 0 });
            gsap.set(backCtrl, { x: '15%', opacity: 0 });
            previewItem.DOM.el.classList.add('preview__item--current');
          },
          onComplete: () => {
            isAnimating = false;
          },
        })
          .addLabel('start', 0)
          .addLabel('preview', 'start+=0.3')
          .to(contentOverlayInner, { ease: 'power2', xPercent: 0 }, 'start')
          .to([previewItem.DOM.img, previewItem.DOM.imgWrap], { xPercent: 0 }, 'preview')
          .to(previewItem.DOM.imgWrap, { opacity: 1 }, 'preview')
          .to(previewItem.DOM.slideTexts, { yPercent: 0, stagger: 0.05 }, 'preview')
          .to(previewItem.DOM.descriptions, { opacity: 1, yPercent: 0, stagger: 0.05 }, 'preview')
          .to(backCtrl, { opacity: 1, x: '-=15%' }, 'preview')
          .to(previewItem.DOM.title, { backgroundColor: 'rgba(19, 19, 18,0.3)', borderRadius: '40px', duration: 0.6, ease: 'power2.out' }, 'preview');
      });

      contentItem.DOM.imgWrap.addEventListener('mouseenter', () => {
        gsap.timeline({ defaults: { duration: 0.6, ease: 'expo' } })
          .addLabel('start', 0)
          .set(contentItem.DOM.titleInner, { transformOrigin: '0% 50%' }, 'start')
          .to(contentItem.DOM.titleInner, {
            startAt: { filter: 'blur(0px)' },
            duration: 0.2,
            ease: 'power1.in',
            yPercent: -100,
            rotation: -4,
            filter: 'blur(6px)',
          }, 'start')
          .to(contentItem.DOM.titleInner, {
            startAt: { yPercent: 100, rotation: 4, filter: 'blur(6px)' },
            yPercent: 0,
            rotation: 0,
            filter: 'blur(0px)',
          }, 'start+=0.2')
          .to(contentItem.DOM.imgWrap, { scale: 0.95 }, 'start')
          .to(contentItem.DOM.img, { scale: 1.2 }, 'start');
      });

      contentItem.DOM.imgWrap.addEventListener('mouseleave', () => {
        gsap.timeline({ defaults: { duration: 0.8, ease: 'power4' } })
          .addLabel('start', 0)
          .to([contentItem.DOM.imgWrap, contentItem.DOM.img], { scale: 1 }, 'start');
      });
    });

    backCtrl.addEventListener('click', () => {
      if (isAnimating) return;
      isAnimating = true;
      const previewItem = previewItems[current];

      gsap.timeline({
        defaults: { duration: 1, ease: 'power4' },
        onComplete: () => {
          previewItem.DOM.el.classList.remove('preview__item--current');
          bodyEl.classList.remove('preview-open');
          isAnimating = false;
        },
      })
        .addLabel('start', 0)
        .to(backCtrl, { opacity: 0 }, 'start')
        .to(previewItem.DOM.descriptions, { opacity: 0, yPercent: 15 }, 'start')
        .to(previewItem.DOM.title, { backgroundColor: 'transparent', borderRadius: '40px', duration: 0.6, ease: 'power2.out' }, 'start')
        .to(previewItem.DOM.slideTexts, { yPercent: 100 }, 'start')
        .to(previewItem.DOM.img, { xPercent: -100 }, 'start')
        .to(previewItem.DOM.imgWrap, {  xPercent: 100, opacity: 0 }, 'start')
        .to(contentOverlayInner, { xPercent: 100 }, 'start+=0.4');
        
    });

    preloadImages('.content__item-img').then(() => {
      bodyEl.classList.remove('loading');
    });
  }, []);

  return (
    <div className="demo-1 loading">
      <div className='main'>
        <div className="content">
          {[
  {
    title: "Documents",
    img: "../img/img/doc.jpg",
    caption: "Professional printing for all your essential documents with crisp quality and fast turnaround."
  },
  {
    title: "Banners",
    img: "../img/img/banner.jpg",
    caption: "Vibrant and durable banners perfect for events, promotions, and outdoor displays."
  },
  {
    title: "Posters",
    img: "../img/img/poster.jpg",
    caption: "High-resolution posters to showcase your ideas, products, or events with impact."
  },
  {
    title: "Business Cards",
    img: "../img/img/vcard1.jpg",
    caption: "Create a lasting first impression with our premium custom business cards."
  },
  {
    title: "Letter Heads",
    img: "../img/img/letterhead.jpg",
    caption: "Enhance your brand identity with professionally printed letterheads."
  },
  {
    title: "Blackbook",
    img: "../img/img/blackbook.jpg",
    caption: "Elegant blackbooks for portfolios, presentations, and personalized gifting."
  },
  {
    title: "Magazines",
    img: "../img/img/mag.jpg",
    caption: "Sleek, polished magazine prints tailored for marketing, publishing, or branding needs."
  },
  {
    title: "Lanyards",
    img: "../img/img/lanyard.jpg",
    caption: "Custom lanyards ideal for corporate events, ID tags, and promotional giveaways."
  }
          ]
.map((item, index) => (
            <figure className="content__item" key={index}>
              <h2 className="content__item-title oh"><span className="oh__inner">{item.title}</span></h2>
              <div className="content__item-img-wrap" style={{ background: `linear-gradient(180deg, #1e4762 0%, #1a2940 100%)`, borderRadius: '20px' }}>
                <div className="content__item-img" style={{ backgroundImage: `url(${item.img})`, borderRadius: '10px' }}></div>
              </div>
              <figcaption className="content__item-caption">{item.caption}</figcaption>
            </figure>
          ))}
        </div>

        <div className="content__overlay">
          <div className="overlay__inner"></div>
        </div>

        <div className="preview">
          {
            [
  {
    title: "Documents",
    subtitle: "Professional Paperwork & Forms",
    year: "2003",
    img: "../img/img/doc.jpg",
    about: "Standard printed documents including agreements, forms, and company communications.",
    recommendedStandards: "A4 size<br>90-100 GSM Uncoated or Matte paper"
  },
  {
    title: "Banners",
    subtitle: "Large Format Event, Promotional Displays",
    year: "2007",
    img: "../img/img/banner.jpg",
    about: "Used for events and advertisements, banners provide high-visibility messaging indoors or outdoors.",
    recommendedStandards: "Vinyl material<br>300-510 GSM<br>UV-resistant ink"
  },
  {
    title: "Posters",
    subtitle: "Advertising and Decorative Prints",
    year: "2007",
    img: "../img/img/poster.jpg",
    about: "Visual materials for branding, campaigns, or artistic expression, designed to attract attention.",
    recommendedStandards: "A3/A2/A1 sizes<br>130-170 GSM Glossy or Matte stock"
  },
  {
    title: "Business Cards",
    subtitle: "Compact Contact & Branding Tools",
    year: "2010",
    img: "../img/img/vcard1.jpg",
    about: "Custom-designed cards for sharing professional contact details with a strong first impression.",
    recommendedStandards: "300-350 GSM Art card, Matte or Gloss laminated"
  },
  {
    title: "Letter Heads",
    subtitle: "Corporate Branded Communication Sheets",
    year: "2021",
    img: "../img/img/letterhead.jpg",
    about: "Printed sheets bearing company identity used for legal and business communications.",
    recommendedStandards: "A4 size<br>100-120 GSM Premium Bond or Executive Paper"
  },
  {
    title: "Blackbooks",
    subtitle: "Custom Branded Notebooks",
    year: "2021",
    img: "../img/img/blackbook.jpg",
    about: "Personalized notebooks used for writing, sketching, journaling, or branding purposes.",
    recommendedStandards: "Hardcover<br>70-90 GSM Inner Pages<br>100+ leaves"
  },
  {
    title: "Magazines",
    subtitle: "Branded Editorial & Product Publications",
    year: "2021",
    img: "../img/img/mag.jpg",
    about: "Multi-page publications with articles, photos, or product listings, used for branding or editorial.",
    recommendedStandards: "A4 size<br>130 GSM inner paper<br>250 GSM Cover, Saddle-stitched or Perfect bound"
  },
  {
    title: "Lanyards",
    subtitle: "Custom ID & Badge Holders",
    year: "2021",
    img: "../img/img/lanyard.jpg",
    about: "Worn around the neck to carry identification badges, ideal for events, offices, or schools.",
    recommendedStandards: "Polyester<br>20mm width<br>Digital sublimation, with breakaway clasp"
  }
].map((item, index) => (
            <div className="preview__item" key={index}>
              <div className="preview__item-img-outer">
                <div className="preview__item-img-wrap" style={{ background: `linear-gradient(180deg, #1e4762 20%, #1a2940 70%)`, borderRadius: '20px' }}>
                  <div className="preview__item-img" style={{ backgroundImage: `url(${item.img})`, borderRadius: '10px' }}></div>
                </div>
              </div>
              <h2 className="preview__item-title oh"><span className="oh__inner">{item.title}</span></h2>
              <h3 className="preview__item-subtitle oh"><span className="oh__inner">{item.subtitle}</span></h3>
              {/* <span className="preview__item-meta oh"><span className="oh__inner">{item.year}</span></span> */}
              <div className="preview__item-box preview__item-box--left">
                <h3 className="preview__item-box-title oh"><span className="oh__inner">Description</span></h3>
                <p className="preview__item-box-desc">{item.about}</p>
              </div>
              <div className="preview__item-box preview__item-box--right">
                <h3 className="preview__item-box-title oh"><span className="oh__inner">Recommended Standards</span></h3>
                <p className="preview__item-box-desc" dangerouslySetInnerHTML={{ __html: item.recommendedStandards }}></p>
              </div>
            </div>
          ))}

          <button className="unbutton preview__back" aria-label="Back to content">
            <svg width="200px" height="36px" viewBox="0 0 50 9">
              <path vectorEffect="non-scaling-stroke" d="m0 4.5 5-3m-5 3 5 3m45-3h-77"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Products;
